interface GpuTimerExtension {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
}

/**
 * Measures the GPU time of one span per frame with `EXT_disjoint_timer_query_webgl2`, when the browser
 * offers it. Results arrive a few frames later; `poll` returns the newest complete one. Without the
 * extension every call is a no-op and `poll` returns null.
 */
export class HsGpuFrameTimer {
  private extension: GpuTimerExtension | null | undefined;
  private active: WebGLQuery | null = null;
  private readonly pending: WebGLQuery[] = [];

  public get available(): boolean {
    return this.extension !== null && this.extension !== undefined;
  }

  public begin(gl: WebGL2RenderingContext): void {
    const extension: GpuTimerExtension | null = this.resolve(gl);
    // One query per frame; a frame that ends before the previous query was read waits for its turn.
    if (extension === null || this.active !== null || this.pending.length > 8) {
      return;
    }
    const query: WebGLQuery | null = gl.createQuery();
    if (query === null) {
      return;
    }
    try {
      gl.beginQuery(extension.TIME_ELAPSED_EXT, query);
      this.active = query;
    } catch {
      gl.deleteQuery(query);
      this.extension = null;
    }
  }

  public end(gl: WebGL2RenderingContext): void {
    if (this.active === null || !this.extension) {
      return;
    }
    try {
      gl.endQuery(this.extension.TIME_ELAPSED_EXT);
      this.pending.push(this.active);
    } catch {
      gl.deleteQuery(this.active);
      this.extension = null;
    }
    this.active = null;
  }

  /** The newest finished measurement in milliseconds, or null when none completed since the last poll. */
  public poll(gl: WebGL2RenderingContext): number | null {
    if (!this.extension || this.pending.length === 0) {
      return null;
    }
    let latest: number | null = null;
    for (let index: number = 0; index < this.pending.length; ) {
      const query: WebGLQuery = this.pending[index];
      const available: boolean = Boolean(gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE));
      if (!available) {
        index++;
        continue;
      }
      const disjoint: boolean = Boolean(gl.getParameter(this.extension.GPU_DISJOINT_EXT));
      if (!disjoint) {
        const nanoseconds: number = Number(gl.getQueryParameter(query, gl.QUERY_RESULT));
        latest = nanoseconds / 1e6;
      }
      gl.deleteQuery(query);
      this.pending.splice(index, 1);
    }
    return latest;
  }

  public dispose(gl: WebGL2RenderingContext): void {
    for (const query of this.pending) {
      gl.deleteQuery(query);
    }
    this.pending.length = 0;
    if (this.active !== null) {
      gl.deleteQuery(this.active);
      this.active = null;
    }
  }

  private resolve(gl: WebGL2RenderingContext): GpuTimerExtension | null {
    if (this.extension === undefined) {
      this.extension = gl.getExtension('EXT_disjoint_timer_query_webgl2') as GpuTimerExtension | null;
    }
    return this.extension;
  }
}
